import { AlertCircle } from 'lucide-react'

import type { FormField } from '@/types/api/form'
import { cn } from '@/utils/cn'

export function FieldInput({ id, label, type, required, value, error, prefix, min, max, step, onChange }: {
  id: string; label: string; type: string; required?: boolean
  value: string; error?: string; prefix?: string
  min?: number; max?: number; step?: number
  onChange: (v: string) => void
}) {
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-sm font-medium text-neutral-700">
        {label}{required && <span className="text-rose-500 ml-0.5">*</span>}
      </label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 text-sm font-medium">
            {prefix}
          </span>
        )}
        <input
          id={id} type={type} value={value} min={min} max={max} step={step}
          onChange={e => onChange(e.target.value)}
          className={cn(
            'w-full h-10 rounded-lg border bg-white px-3 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:border-transparent transition-colors',
            prefix && 'pl-7',
            error ? 'border-rose-300 focus:ring-rose-400' : 'border-neutral-200 focus:ring-primary-400'
          )}
        />
      </div>
      {error && (
        <p className="flex items-center gap-1 text-xs text-rose-600">
          <AlertCircle className="h-3 w-3" />{error}
        </p>
      )}
    </div>
  )
}

export function SelectFieldInput({ field, choices, value, error, onChange }: {
  field: FormField; choices: string[]; value: string; error?: string
  onChange: (v: string) => void
}) {
  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-neutral-700">
        {field.label}{field.required && <span className="text-rose-500 ml-0.5">*</span>}
      </label>
      <select
        value={value} onChange={e => onChange(e.target.value)}
        className={cn(
          'w-full h-10 rounded-lg border bg-white px-3 text-sm text-neutral-900 focus:outline-none focus:ring-2 focus:border-transparent',
          error ? 'border-rose-300 focus:ring-rose-400' : 'border-neutral-200 focus:ring-primary-400'
        )}
      >
        <option value="">Select an option...</option>
        {choices.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
      {error && (
        <p className="flex items-center gap-1 text-xs text-rose-600">
          <AlertCircle className="h-3 w-3" />{error}
        </p>
      )}
    </div>
  )
}

export function ChoiceFieldInput({ field, choices, value, error, multiple, onChange }: { field: FormField; choices: string[]; value: string | string[]; error?: string; multiple: boolean; onChange: (v: string) => void }) {
  const selected = Array.isArray(value) ? value : [value]
  return <div className="space-y-1"><label className="block text-sm font-medium text-neutral-700">{field.label}{field.required && <span className="text-rose-500 ml-0.5">*</span>}</label><div className="space-y-2">{choices.map(choice => <label key={choice} className="flex items-center gap-2 text-sm text-neutral-700"><input type={multiple ? 'checkbox' : 'radio'} name={field.id} checked={selected.includes(choice)} onChange={e => { const next = multiple ? (e.target.checked ? [...selected.filter(Boolean), choice] : selected.filter(v => v !== choice)) : [choice]; onChange(multiple ? JSON.stringify(next) : choice) }} />{choice}</label>)}</div>{error && <p className="flex items-center gap-1 text-xs text-rose-600"><AlertCircle className="h-3 w-3" />{error}</p>}</div>
}

export function BooleanFieldInput({ field, value, error, onChange }: { field: FormField; value: string; error?: string; onChange: (v: string) => void }) {
  const checked = value === 'true'
  return <div className="space-y-1"><label className="flex items-center gap-2 text-sm font-medium text-neutral-700"><input type="checkbox" checked={checked} onChange={e => onChange(String(e.target.checked))} />{field.label}{field.required && <span className="text-rose-500">*</span>}</label>{error && <p className="flex items-center gap-1 text-xs text-rose-600"><AlertCircle className="h-3 w-3" />{error}</p>}</div>
}

